import { FC, useMemo } from 'react';
import { useWindowSize } from 'react-use';

import container from 'atoms/spacing/containers';

// Quarks
import Flex from 'quarks/Flex';
import Image, { ImageData } from 'quarks/Image';
import type { FlexProps } from 'quarks/interpolations/flex';

import Form from 'components/Form';
import ComponentHeading from 'components/Heading/Heading';

// Types
import type { ContentfulComponentNewsletterCallToAction } from 'graphqlTypes';

interface ConversionPanelProps extends FlexProps, ContentfulComponentNewsletterCallToAction {
  backgroundIsDark: boolean;
  newsHeading?: ContentfulComponentNewsletterCallToAction['heading'];
  emailSubscribe?: boolean;
}

const ComponentConversionPanel: FC<ConversionPanelProps> = ({
  newsHeading,
  backgroundDesktop,
  backgroundTablet,
  backgroundMobile,
  backgroundIsDark,
  image,
  emailSubscribe,
  ...props
}) => {
  const { width } = useWindowSize();
  const desktopSize = Number(container.lg.replace('px', ''));
  const tabletSize = Number(container.md.replace('px', ''));

  const renderBackground = useMemo(() => {
    if (width > desktopSize) {
      return backgroundDesktop;
    } else if (width > tabletSize) {
      return backgroundTablet;
    } else {
      return backgroundMobile;
    }
  }, [width]);

  return (
    <Flex position="relative" width="100%" borderRadius="20px" overflowX="hidden" overflowY="hidden">
      <Flex
        position="relative"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        zIndex="2"
        paddingY={40}
        paddingX={24}
        md={{ paddingY: 64, paddingX: 48 }}
        lg={{ paddingX: 64 }}
        {...props}
      >
        <Flex flexDirection="column" gap="24px">
          {newsHeading && (
            <Flex width="100%" maxWidth="100%" md={{ maxWidth: '370px' }} lg={{ maxWidth: '570px' }}>
              <ComponentHeading isLeft backgroundIsDark={backgroundIsDark} {...newsHeading} />
            </Flex>
          )}
          {emailSubscribe && (
            <Flex width="100%" maxWidth="100%" md={{ maxWidth: '370px' }} lg={{ maxWidth: '570px' }}>
              <Form formType="Inline Subscribe" buttonLabel="Subscribe" />
            </Flex>
          )}
        </Flex>
        {image && (
          <Flex width="100%" maxWidth="352px" display="none" md={{ display: 'flex' }}>
            <Image
              image={(image?.bynderImageDesktop || image?.imageDesktop) as ImageData}
              alt="Newsletter CTA Accent Image"
              boxShadow={image?.boxShadow}
              width="100%"
              roundedCorners={image?.roundedCorners}
            />
          </Flex>
        )}
      </Flex>
      <Flex position="absolute" width="100%" height="100%" top="0" left="0">
        <Image
          image={renderBackground as ImageData}
          alt="Newsletter CTA"
          width="100%"
          height="100%"
          objectFit="cover"
          objectPosition="center"
        />
      </Flex>
    </Flex>
  );
};

export default ComponentConversionPanel;
